import { useCallback, useMemo } from 'react';
import {
  FilterValue,
  SorterResult,
  TablePaginationConfig,
} from 'antd/es/table/interface';
import { TSortParams } from 'data/types/generic.types';
import {
  DEFAULT_DATE_SORT_COLUMN,
  isSortedByNextSessionDate,
} from './sessionDateSort.helpers';

type TTableChangeHandler<T> = (
  pagination: TablePaginationConfig,
  filters: Record<string, FilterValue | null>,
  sorter: SorterResult<T> | SorterResult<T>[],
) => void;

type TUseSessionDateSortParams<T> = {
  sortColumn: TSortParams;
  setSortColumn: (sortColumn: TSortParams) => void;
  setPage: (page: number) => void;
  handleTableChange: TTableChangeHandler<T>;
};

// Sorting for the Workshops "Session Date" / Events "Event Date" columns.
// The column is a regular antd sorter keyed on the backend's PART_DATE field,
// so useTable sends it as sortField + sortDirection like any other column.
// The tables always have a sort: cycling the date column (or any column) off
// returns to the default, next session date ascending (GROW-4270).
const useSessionDateSort = <T>({
  sortColumn,
  setSortColumn,
  setPage,
  handleTableChange,
}: TUseSessionDateSortParams<T>) => {
  const isDateSorted = useMemo(
    () => isSortedByNextSessionDate(sortColumn),
    [sortColumn],
  );

  // For selections above the table that change which records it lists (e.g.
  // the "Show only my Events" toggle): drop whatever sort is active and return
  // to the default and the first page in the same render, so the request for
  // the new selection never carries the stale sort (GROW-4271).
  const resetSort = useCallback(() => {
    setSortColumn(DEFAULT_DATE_SORT_COLUMN);
    setPage(0);
  }, [setSortColumn, setPage]);

  const onTableChange: TTableChangeHandler<T> = useCallback(
    (pagination, filters, sorter) => {
      // antd reports "no sort" when a column sort is cycled off; instead of
      // letting useTable clear the sort, fall back to the default order.
      if (!Array.isArray(sorter) && !sorter?.order) {
        handleTableChange(pagination, filters, {
          columnKey: DEFAULT_DATE_SORT_COLUMN.sortField,
          order: 'ascend',
        } as SorterResult<T>);
        return;
      }
      handleTableChange(pagination, filters, sorter);
    },
    [handleTableChange],
  );

  return { isDateSorted, resetSort, onTableChange };
};

export default useSessionDateSort;
